import React, { Component, ChangeEvent } from 'react';
import { Formik } from 'formik';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';
import { PageView } from '@jg/react-tracking';
import { Trans, withTranslation } from 'react-i18next';
import {
  getRequestError,
  getRequestInFlight,
} from '../../selectors/httpSelectors';
import StepNavigation from '../../components/StepNavigation';
import { IAppState } from '../../types/state.d';
import {
  apiActions,
  apiThunk as apiThunkAction,
  appThunk as appThunkAction,
  httpActions,
} from '../../actions';
import {
  ROUTES,
  REQUEST,
  SHORT_NAME_MINIMUM_CHARACTER,
  SHORT_NAME_MAXIMUM_CHARACTER,
  TARGET_MIN,
  TARGET_MAX,
} from '../../consts';
import {
  TTeamSettings,
  ITeamSettingsDispatchToProps,
  ITeamSettingsMapStateToProps,
  ITeamSettingsState,
  ITeamSettingsFormProps,
} from './TeamSettings.d';
import TeamSettingsForm from '../../components/TeamSettingsForm';
import {
  getShortName,
  getTeamFundraisingTarget,
  isShortNameValid as teamSlugValid,
  getCurrencyCode,
  getTeamStatus,
  getTeamMembersTargetAmount,
} from '../../selectors/apiSelectors';
import FatalError from '../../components/FatalError';

export class TeamSettings extends Component<TTeamSettings, ITeamSettingsState> {
  setTimeoutId = 0;

  state = {
    setTeamMembersTarget: false,
  };

  onBackButtonClick = () => {
    const { pushToPage } = this.props;
    pushToPage(ROUTES.STORY);
  };

  handleOnKeyUp = (
    event: ChangeEvent<any> & {
      target: HTMLInputElement;
    },
  ) => {
    const { checkShortName } = this.props;
    event.persist();
    window.clearTimeout(this.setTimeoutId);
    this.setTimeoutId = window.setTimeout(() => {
      checkShortName(event.target.value);
    }, 300);
  };

  handleSubmit = (data: ITeamSettingsFormProps) => {
    const { updateTeamSettings, isShortNameValid, teamStatus } = this.props;
    if (isShortNameValid !== true && teamStatus !== 'active') {
      return;
    }
    updateTeamSettings(data);
  };

  handleClick = () => {
    const setTeamMembersTarget = this.state.setTeamMembersTarget;
    this.setState({ setTeamMembersTarget: !setTeamMembersTarget });
  };

  render() {
    const {
      t,
      isError,
      isShortNameValid,
      currencyCode,
      shortName,
      isInFlight,
      teamFundraisingTarget,
      teamMembersTarget,
      teamStatus,
      resetRequestError,
      shortNameCheckIsError,
      shortNameCheckIsInFlight,
      clearShortNameValidity,
    } = this.props;
    return (
      <PageView
        trackingData={{
          subtype: 'page creation',
          event_value: 'team settings',
          page_section: 'page creation > team settings',
        }}
      >
        <Helmet title={t('title.createTeam.yourTeamSettings')} />
        <main>
          <StepNavigation
            onBackButtonClick={this.onBackButtonClick}
            currentStep={4}
            trackingData={{
              action_name: 'link click',
              subtype: 'button',
              event: 'link click',
              event_value: 'back',
              page_section: 'page creation > team settings',
            }}
          />
          <h1 className="container--heading jg-space-mbxs">
            <Trans i18nKey="content.teamsettings.heading" />
          </h1>
          <p className="jg-text--large container--heading jg-space-mbml">
            <Trans i18nKey="content.teamsettings.headingsubtext" />
          </p>
          <Formik<ITeamSettingsFormProps>
            initialValues={{
              shortName,
              teamFundraisingTarget,
              teamMembersTarget,
            }}
            validationSchema={Yup.object().shape({
              shortName: Yup.string()
                .required(
                  t('content.teamsettings.shortname.teampagelinkrequired'),
                )
                .min(
                  SHORT_NAME_MINIMUM_CHARACTER,
                  t('content.teamsettings.shortname.teampagelinkmincharacter'),
                )
                .max(
                  SHORT_NAME_MAXIMUM_CHARACTER,
                  t('content.teamsettings.shortname.teampagelinkmaxcharacter'),
                )
                .matches(
                  /^[a-z0-9]{1}[a-z0-9-]+$/i,
                  t('content.teamsettings.shortname.teampagelinknotallowed'),
                ),
              teamFundraisingTarget: Yup.number()
                .min(
                  TARGET_MIN,
                  t('content.teamsettings.teamfundraisingtargetmin'),
                )
                .max(
                  TARGET_MAX,
                  t('content.teamsettings.teamfundraisingtargetmax'),
                ),
            })}
            onSubmit={this.handleSubmit}
            render={
              /* istanbul ignore next */ formikProps => (
                <TeamSettingsForm
                  handleClick={this.handleClick}
                  setTeamMembersTarget={this.state.setTeamMembersTarget}
                  {...formikProps}
                  handleOnKeyUp={this.handleOnKeyUp}
                  isShortNameValid={isShortNameValid}
                  currencyCode={currencyCode}
                  isInFlight={isInFlight}
                  teamStatus={teamStatus}
                  clearShortNameValidity={clearShortNameValidity}
                  shortNameCheckIsInFlight={shortNameCheckIsInFlight}
                  shortNameCheckIsError={shortNameCheckIsError}
                />
              )
            }
          />
          {(isError || shortNameCheckIsError) && (
            <FatalError closeButtonClick={resetRequestError} />
          )}
        </main>
      </PageView>
    );
  }
}

export const mapDispatchToProps: ITeamSettingsDispatchToProps = {
  updateTeamSettings: apiThunkAction.updateTeamSettings,
  checkShortName: apiThunkAction.checkShortName,
  pushToPage: appThunkAction.pushToPage,
  resetRequestError: httpActions.resetRequestError,
  clearShortNameValidity: apiActions.clearShortNameValidity,
};

const mapStateToProps = (state: IAppState): ITeamSettingsMapStateToProps => ({
  isInFlight: getRequestInFlight(REQUEST.UPDATE_TEAM_SETTINGS)(state),
  isError: getRequestError(REQUEST.UPDATE_TEAM_SETTINGS)(state),
  shortName: getShortName(state),
  teamFundraisingTarget: getTeamFundraisingTarget(state),
  isShortNameValid: teamSlugValid(state),
  currencyCode: getCurrencyCode(state),
  teamStatus: getTeamStatus(state),
  teamMembersTarget: getTeamMembersTargetAmount(state),
  shortNameCheckIsInFlight: getRequestInFlight(REQUEST.CHECK_SHORT_NAME)(state),
  shortNameCheckIsError: getRequestError(REQUEST.CHECK_SHORT_NAME)(state),
});

export default withTranslation()(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(TeamSettings),
);

// TODO Move shortname functionality inside form?
